import React, { useState, useRef, UIEvent } from 'react';
import {
  Modal,
  Button,
  Tag,
  Alert,
  Spin,
  Input,
  Space,
  Typography,
} from 'antd';
import {
  SafetyCertificateFilled,
  CalendarOutlined,
  CheckCircleFilled,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  DownOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { PrivacyPolicy } from '../../types/privacy-policy';
import './PrivacyPolicyModal.css';

const { Text } = Typography;

export interface PrivacyPolicyModalProps {
  open: boolean;
  policy: PrivacyPolicy | null;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onAccept: () => Promise<void>;
  onReject: (reason?: string) => Promise<void>;
}

/** Formats ISO date string to readable format e.g. "Oct 16, 2026" */
function formatEffectiveDate(dateStr?: string): string {
  if (!dateStr) return 'Immediately';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/**
 * Lightweight markdown-like formatter for policy text.
 * Renders headings, bold text, bullet items, and clean paragraphs.
 */
const PolicyContentRenderer: React.FC<{ content: string }> = ({ content }) => {
  const lines = content.split('\n');

  return (
    <div className="privacy-policy-content-body">
      {lines.map((line, idx) => {
        const trimmed = line.trim();

        if (!trimmed) {
          return <div key={idx} style={{ height: 10 }} />;
        }

        // H1
        if (trimmed.startsWith('# ')) {
          return <h1 key={idx}>{trimmed.replace(/^#\s+/, '')}</h1>;
        }

        // H2
        if (trimmed.startsWith('## ')) {
          return <h2 key={idx}>{trimmed.replace(/^##\s+/, '')}</h2>;
        }

        // H3
        if (trimmed.startsWith('### ')) {
          return <h3 key={idx}>{trimmed.replace(/^###\s+/, '')}</h3>;
        }

        // Bullet point
        if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
          const itemText = trimmed.replace(/^[\*\-]\s+/, '');
          return (
            <ul key={idx} style={{ margin: '4px 0' }}>
              <li>{renderFormattedText(itemText)}</li>
            </ul>
          );
        }

        // Regular paragraph with bold support
        return <p key={idx}>{renderFormattedText(trimmed)}</p>;
      })}
    </div>
  );
};

/** Render bold text with **bold** */
function renderFormattedText(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

export const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({
  open,
  policy,
  loading = false,
  error = null,
  onRetry,
  onAccept,
  onReject,
}) => {
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isAtBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 24;
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAcceptClick = async () => {
    setAccepting(true);
    try {
      await onAccept();
    } finally {
      setAccepting(false);
    }
  };

  const handleConfirmReject = async () => {
    setRejecting(true);
    try {
      await onReject(rejectReason.trim() || undefined);
    } finally {
      setRejecting(false);
    }
  };

  const versionText = policy?.version !== undefined ? `v${policy.version}` : 'v1';
  const effectiveDate = formatEffectiveDate(policy?.effectiveAt);

  return (
    <Modal
      open={open}
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={720}
      wrapClassName="privacy-policy-modal-wrap"
      className="privacy-policy-modal"
    >
      {/* Header */}
      <div className="privacy-policy-header">
        <div className="privacy-policy-icon-wrapper">
          <SafetyCertificateFilled />
        </div>
        <div className="privacy-policy-header-text">
          <div className="privacy-policy-title-row">
            <h2 className="privacy-policy-title">
              {policy?.title || 'Privacy Policy'}
            </h2>
            <Tag color="geekblue" className="privacy-policy-version-tag">
              {versionText}
            </Tag>
            <Tag color="blue" bordered={false}>
              Required Consent
            </Tag>
          </div>
          <div className="privacy-policy-meta-row">
            <span>
              <CalendarOutlined style={{ marginRight: 5 }} />
              Effective: <strong>{effectiveDate}</strong>
            </span>
          </div>
        </div>
      </div>

      {/* Notice Alert */}
      <Alert
        message="Privacy Policy Acceptance Required"
        description="We take your privacy seriously. Please review the updated policy below. You must accept these terms to continue accessing the application."
        type="info"
        showIcon
        className="privacy-policy-notice-banner"
      />

      {/* Content Viewport */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: '#64748b' }}>
            Loading latest privacy policy terms...
          </div>
        </div>
      ) : error ? (
        <Alert
          message="Failed to Load Privacy Policy"
          description={error}
          type="error"
          showIcon
          action={
            onRetry ? (
              <Button size="small" type="primary" icon={<ReloadOutlined />} onClick={onRetry}>
                Retry
              </Button>
            ) : undefined
          }
          style={{ margin: '20px 0' }}
        />
      ) : policy?.content ? (
        <>
          <div
            ref={scrollRef}
            className="privacy-policy-scroll-viewport"
            onScroll={handleScroll}
          >
            <PolicyContentRenderer content={policy.content} />
          </div>
          <div className="privacy-policy-scroll-hint">
            {hasScrolledToBottom ? (
              <span style={{ color: '#52c41a', display: 'flex', alignItems: 'center', gap: 4 }}>
                <CheckCircleOutlined /> You have reviewed the policy content
              </span>
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <DownOutlined /> Scroll to read all sections
              </span>
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>
              Version {policy.version}
            </Text>
          </div>
        </>
      ) : (
        <div style={{ padding: '30px 20px', textAlign: 'center', color: '#64748b' }}>
          No policy content available.
        </div>
      )}

      {/* Rejection Confirmation Block */}
      {showRejectConfirm ? (
        <div className="privacy-policy-reject-box">
          <div className="privacy-policy-reject-box-title">
            <ExclamationCircleOutlined /> Are you sure you want to decline?
          </div>
          <p style={{ margin: '0 0 10px 0', fontSize: 13, color: '#595959' }}>
            Acceptance is required to use this service. Declining the privacy policy will
            terminate your session and log you out immediately.
          </p>
          <Input
            placeholder="Optional: Reason for declining (e.g. Declined analytics data sharing)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            disabled={rejecting}
            style={{ marginBottom: 12 }}
            maxLength={250}
          />
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => setShowRejectConfirm(false)}
              disabled={rejecting}
            >
              Back to Review
            </Button>
            <Button
              danger
              type="primary"
              loading={rejecting}
              icon={<CloseCircleOutlined />}
              onClick={handleConfirmReject}
            >
              Confirm & Decline Policy
            </Button>
          </Space>
        </div>
      ) : (
        /* Action Footer */
        <div className="privacy-policy-footer">
          <Button
            danger
            onClick={() => setShowRejectConfirm(true)}
            disabled={accepting || loading}
            icon={<CloseCircleOutlined />}
          >
            Decline Policy
          </Button>

          <Button
            type="primary"
            size="large"
            loading={accepting}
            disabled={loading || !policy}
            icon={<CheckCircleFilled />}
            onClick={handleAcceptClick}
            style={{ minWidth: 200 }}
          >
            I Accept the Privacy Policy
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default PrivacyPolicyModal;
